import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod, resolveComparePeriod } from '../../components/performance/pmAnalyticsFilters';
import { useAuth } from '../../context/AuthContext';
import { Badge, Grid, PageEmpty, PageWrap, TabButton, Tabs } from '../performance-operations/styles';
import { anyStale, fetchPeriods, oldestCalculatedAt } from '../performance-operations/config';
import ResultKpiCards from './shared/ResultKpiCards';
import DistributionPanel from './shared/DistributionPanel';
import ComponentsPanel from './components/ComponentsPanel';
import EmployeeList from './components/EmployeeList';
import { Q, RESULT_KPI_CODES, trendsLink, usePageParams } from './config';

// PM analitik P2 — E03 Performans Sonuçları (/performance/results). İK ve yetkili yönetici: İK kiracıyı, yönetici kendi alt
// ağacını görür (kapsam sunucuda; sonuç değerleri yöneticinin tek tek gördüğü kişilerden). Varsayılan puan hali yayımlanmış/
// kalibre FINAL; ön sonuç yalnız İK'ya, ayrı bir görünüm olarak (final ile aynı ortalamada/dağılımda birleşmez). Üstte M06, M07,
// M08, M51; ortada puan bandı (önceki karşılaştırılabilir dönem ikinci seri) ve bileşenler; altta çalışan tablosu.
// Bant → aynı süzgeçli liste → E14. Süzgeçler ve seçimler URL'dedir (geri dönüşte korunur). Panel başına bağımsız yükleme/hata.
const StateBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export default function PerformanceResultsPage() {
  const { t } = useTranslation();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr } = useAuth();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [params, setParams] = usePageParams(Object.values(Q));
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});
  const [distribution, setDistribution] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);

  // Ön sonuç yalnız İK'ya (sunucu da 403 verir); URL'de kalsa bile yöneticide final görünür.
  const prelim = isHr && params[Q.prelim] === '1';
  const page = useMemo(() => ({
    band: params[Q.band], bucket: params[Q.bucket], goalBand: params[Q.goalBand],
    calibrationChanged: params[Q.calibrationChanged], published: params[Q.published], missing: params[Q.missing],
  }), [params]);
  const comparePeriod = useMemo(() => resolveComparePeriod(periods, filters), [periods, filters]);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);
  const trends = trendsLink(filters);

  const setScoreState = (next) => {
    // Bant ve kalibrasyon süzgeçleri yalnız finalde anlamlıdır.
    setParams(next === 'preliminary'
      ? { [Q.prelim]: '1', [Q.band]: null, [Q.calibrationChanged]: null, [Q.bucket]: null }
      : { [Q.prelim]: null, [Q.bucket]: null });
  };

  const selectBucket = (kind, bucket) => {
    if (kind === 'bands') setParams({ [Q.band]: bucket.key, [Q.bucket]: null, [Q.missing]: null });
    else if (kind === 'goalBands') setParams({ [Q.goalBand]: bucket.key });
    else setParams({ [Q.bucket]: bucket.key, [Q.band]: null, [Q.missing]: null });
  };

  // KPI → liste: yüksek/düşük performans bandın listesine, kapsama kartı sonucu eksik kişilere iner.
  const selectKpi = (code) => {
    if (code === 'M07') setParams({ [Q.band]: 'high', [Q.bucket]: null, [Q.missing]: null });
    else if (code === 'M08') setParams({ [Q.band]: 'low', [Q.bucket]: null, [Q.missing]: null });
    else if (code === 'M51') setParams({ [Q.missing]: 'true', [Q.band]: null, [Q.bucket]: null });
  };

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('performance.results.analytics.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.results.analytics.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <StateBar>
          <Badge $tone={prelim ? 'info' : 'success'} data-score-state={prelim ? 'preliminary' : 'final'}>
            {prelim ? t('performance.results.analytics.badge.preliminary') : t('performance.results.analytics.badge.final')}
          </Badge>
          {isHr ? (
            <Tabs role="tablist" style={{ marginBottom: 0 }}>
              {['final', 'preliminary'].map((s) => (
                <TabButton
                  key={s} role="tab" aria-selected={(s === 'preliminary') === prelim} $active={(s === 'preliminary') === prelim}
                  onClick={() => setScoreState(s)} data-score-tab={s}
                >
                  {t(`performance.results.analytics.scoreState.${s}`)}
                </TabButton>
              ))}
            </Tabs>
          ) : null}
          {trends ? <Link to={trends} data-link="trends">{t('performance.results.analytics.trendsLink')}</Link> : null}
        </StateBar>
      ) : null}
      {ready ? (
        <Grid key={filters.periodId}>
          <ResultKpiCards
            filters={filters} codes={RESULT_KPI_CODES} comparePeriod={comparePeriod} onMeta={onMeta} span={3}
            onSelect={selectKpi}
          />
          <DistributionPanel
            filters={filters} panelKey="distribution" span={6} prelim={prelim}
            title={prelim ? t('performance.results.analytics.dist.titlePreliminary') : t('performance.results.analytics.dist.title')}
            kinds={prelim ? ['levels'] : ['bands', 'levels']} comparePeriod={comparePeriod}
            onSelect={selectBucket} canShowPreliminary={isHr} onShowPreliminary={() => setScoreState('preliminary')}
            onMeta={onMeta} onData={setDistribution}
          />
          <ComponentsPanel filters={filters} prelim={prelim} span={6} onMeta={onMeta} />
          <EmployeeList
            filters={filters} page={page} setPage={setParams} prelim={prelim} isHr={isHr}
            comparePeriod={comparePeriod} distribution={distribution}
          />
        </Grid>
      ) : null}
    </PageWrap>
  );
}
